import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import moment from 'moment';
import {
  Paper, Button, TextField, Grow, Box, Tooltip, Alert,
} from '@mui/material';

import ContentBox from '../components/layout/ContentBox';
import ParticipantList from '../components/trialdetails/ParticipantList';
import EvaluationDialog, { ExtendedEnrollment } from './EvaluationDialog';
import ReviewDialog from './ReviewDialog';
import DetailsHeader from '../components/trialdetails/DetailsHeader';
import DownloadDialog from '../components/DownloadDialog';
import { downloadFromContainer, redirectInternalNewTab } from '../utilities/urlHelper';
import PostResubmitDialog from '../components/trial/PostResubmitDialog';
import GenericDialog from '../components/GenericDialog';
import EnrollInternalUserDialog from '../components/trial/EnrollInternalUserDialog';
import BulkInternalEnrollDialog from '../components/trial/BulkInternalEnrollDialog';
import RegisteredUsersNotifyDialog from '../components/trial/RegisteredUsersNotifyDialog';
import ApiClient from '../loopback/apiClient';
import {
  Survey,
  RootState,
  Certificate,
  Invoice,
  Trial,
  Review,
  ReviewRequest,
  ThirdPartyTrial,
  DetailedEvaluation,
  TrialCategoryValue,
  AggregatedSubmission,
} from '../redux/types';
import {
  approveEnrollment,
  rejectEnrollment,
  cancelEnrollment,
  cancelInvoice,
  approveCertificate,
  recreateCertificate,
  unapproveCertificate,
  showErrorNotification,
  showNotification,
  setLoading,
  fetchDigitalGroundTruthGroups,
} from '../redux/actions';
import DigitalEvaluationDialogDRT from './DigitalEvaluationDialogDRT';
import withRouter, { RouteType } from '../hoc/withRouter';
import { AuswertungSetting } from '../redux/types/auswertungSettingTypes';
import FileUploadDialog from '../components/trial/FileUploadDialog';
import { CERTIFICATES_CONTAINER_NAME } from '../environment';
import Prompt from '../components/layout/Prompt';
import { selectIsEditorPrivileged } from '../redux/reducers/Users';
import { downloadExcelSheets } from '../components/utilities/ExcelExport';

type ExtendedTrial = Trial & {
  survey?: Survey,
  thirdPartyTrial?: ThirdPartyTrial,
};

type Uploader = {
  show: boolean;
  enrollment: ExtendedEnrollment;
  container: string;
}

type State = {
  trialId: string;
  downloadInvoiceFilename?: string;
  replaceFileName?: string;
  detailedEvaluation?: DetailedEvaluation;
  showInternalUserDialog: boolean;
  currentCertificate?: Certificate;
  extendedEnrollments?: ExtendedEnrollment[];
  trialWithSurvey?: ExtendedTrial;
  selectedReview?: Review & {
    reviewRequest?: ReviewRequest;
  };
  unapproveCertificate?: Certificate;
  unapproveCertificteReason?: string;
  enrollmentUserId?: number;
  currentRole?: string;
  showAllowPostResubmitDialog: boolean;
  showDigitalEvaluation: boolean;
  aggregatedSubmission?: AggregatedSubmission;
  auswertungSetting?: AuswertungSetting;
  uploader?: Uploader;
  promptCancel?: {
    trigger: 'invoice' | 'enrollment';
    enrollmentId: number;
  };
  showBulkEnrollDialog: boolean;
  showNotifyRegisteredDialog: boolean;
}

class TrialDetailsView extends Component<Props, State> {
  static downloadInvoiceDisabled = (invoices: Invoice[]) => !invoices
    || invoices.length === 0 || invoices.some((invoice) => !invoice.fileName);

  static isPostResubmitAllowed = (enrollment: ExtendedEnrollment, trial: Trial) => {
    if (moment.utc().isBefore(moment.utc(trial.endDate).add(1, 'day'))) {
      return false;
    }
    return true;
  };

  static isPostResubmitActive = (enrollment: ExtendedEnrollment) => {
    if (!enrollment?.allowPostResubmit) {
      return false;
    }

    const { extendedSubmissionDate } = enrollment;
    return extendedSubmissionDate && moment.utc(extendedSubmissionDate).add(1, 'day').isSameOrAfter(moment.utc());
  };

  constructor(props: Props) {
    super(props);
    const { router } = this.props;
    this.state = {
      // participants: [],
      trialId: router.params.trialId,
      downloadInvoiceFilename: undefined,
      replaceFileName: undefined,
      detailedEvaluation: undefined,
      showInternalUserDialog: false,
      showAllowPostResubmitDialog: false,
      showDigitalEvaluation: false,
      showBulkEnrollDialog: false,
      showNotifyRegisteredDialog: false,
    };
  }

  componentDidMount() {
    const { locale, fetchDigitalGroundTruthGroup } = this.props;
    const { trialId } = this.state;
    ApiClient.getAuswertungSetting(Number(trialId))
      .then((s) => this.setState({ auswertungSetting: s }));
    ApiClient.getTrialWithSurvey(Number(trialId), locale)
      .then((trialWithSurvey) => {
        this.setState({ trialWithSurvey });
        return trialWithSurvey;
      }).then((trial) => {
        if (trial.version && trial.version > 1 && trial.digitalSurveys) {
          fetchDigitalGroundTruthGroup(trial.digitalSurveys[0].id);
        }
      });

    this.fetchExtendedEnrollmentsToState();
  }

  promptHandler = (confirm: boolean) => {
    const { promptCancel } = this.state;
    if (confirm === false || !promptCancel) {
      this.setState({ promptCancel: undefined });
      return;
    }
    if (promptCancel.trigger === 'invoice') {
      this.cancelInvoice(promptCancel.enrollmentId);
    } else if (promptCancel.trigger === 'enrollment') {
      this.cancelEnrollment(promptCancel.enrollmentId);
    }
    this.setState({ promptCancel: undefined });
  };

  onUploadCertificate = (enrollment: ExtendedEnrollment) => {
    const setUploader: Uploader = {
      show: true,
      enrollment,
      container: CERTIFICATES_CONTAINER_NAME,
    };
    this.setState({ uploader: setUploader });
  };

  onCloseUploadCertificateDialog = () => {
    this.setState({ uploader: undefined });
  };

  fetchExtendedEnrollmentsToState = () => {
    const { setLoading: dispatchSetLoading } = this.props;
    const { trialId } = this.state;
    const includes = {
      include: [
        'invoices',
        'certificate',
        { submission: { review: 'reviewRequest' } },
        'surveySubmissionMeta',
        { quipUser: 'institute' },
        'thirdPartyEnrollment',
        'digitalSubmissionMeta',
      ],
    };
    dispatchSetLoading(true);
    ApiClient.getEnrollments<ExtendedEnrollment>(Number(trialId), {}, includes)
      .then((extendedEnrollments) => this.setState({ extendedEnrollments }))
      .finally(() => dispatchSetLoading(false));
  };

  onEnrollInternalUserClick = () => {
    this.setState({ showInternalUserDialog: true });
  };

  closeInternalTrialEnrollDialog = () => {
    this.setState({ showInternalUserDialog: false, enrollmentUserId: undefined });
  };

  onBulkEnrollClick = () => {
    this.setState({ showBulkEnrollDialog: true });
  };

  closeBulkEnrollDialog = () => {
    this.setState({ showBulkEnrollDialog: false });
  };

  onNotifyRegisteredUsersClick = () => {
    this.setState({ showNotifyRegisteredDialog: true });
  };

  closeNotifyRegisteredDialog = () => {
    this.setState({ showNotifyRegisteredDialog: false });
  };

  handleNotifyRegisteredSendEmail = async (payload: { enrollmentIds: number[] }) => {
    const { trialWithSurvey } = this.state;
    const {
      setLoading: setLoadingAction,
      showNotification: showNotificationAction,
      showErrorNotification: showErrorNotificationAction,
    } = this.props;
    if (!trialWithSurvey) {
      return;
    }
    setLoadingAction(true);
    try {
      await ApiClient.notifyRegisteredUsers(trialWithSurvey.id, payload.enrollmentIds);
      showNotificationAction(<FormattedMessage id="internalTrial.notifySendPrepared" />);
      this.closeNotifyRegisteredDialog();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'internalTrial.notifySendError';
      showErrorNotificationAction(message);
    } finally {
      setLoadingAction(false);
    }
  };

  showSubmission = (enrollment: ExtendedEnrollment, trialWithSurvey: ExtendedTrial) => {
    const { router } = this.props;
    const { submission } = enrollment;
    const { survey, version } = trialWithSurvey;
    if (submission && survey && version !== 2) {
      router.navigate(`/showSubmission/${survey.trialId}`, {
        state: {
          submission,
          survey,
        },
      });
    }
    if (version === 2) {
      redirectInternalNewTab(`showDigitalSubmission/trial/${trialWithSurvey.id}/enrollment/${enrollment.id}`);
    }
  };

  getInvoicePdfFileName = (invoice: Invoice) => {
    const { showErrorNotification: showErrorNotificationAction } = this.props;
    ApiClient.getInvoicePdf(invoice.id)
      .then((fileName) => {
        this.setState({
          downloadInvoiceFilename: fileName,
          replaceFileName: invoice.invoiceNumber,
        });
      })
      .catch((e) => {
        if (e.message === 'NO_TRANSACTION_ID') {
          showErrorNotificationAction(<FormattedMessage id="payment.inProcess" />);
        } else {
          showErrorNotificationAction(e.message);
        }
      });
  };

  getEInvoiceFileName = (invoice: Invoice) => {
    const { showErrorNotification: showErrorNotificationAction } = this.props;
    ApiClient.getEInvoice(invoice.id)
      .then((fileName) => {
        downloadFromContainer('invoices', fileName);
      })
      .catch((e) => {
        showErrorNotificationAction(e.message);
      });
  };

  downloadCertificateBundle = () => {
    const { trialId } = this.state;
    const { showErrorNotification: showErrorNotificationAction } = this.props;
    ApiClient.downloadCertificateBundle(Number(trialId))
      .then((val) => {
        const dlPath = val.bundlePath;
        downloadFromContainer('certificates', dlPath);
      })
      .catch((e: Error) => showErrorNotificationAction(e.message));
  };

  uploadCertificateHandler = async (input: HTMLInputElement | null) => {
    const { uploader, trialId } = this.state;
    const { showErrorNotification: showErrorNotificationAction } = this.props;
    if (!uploader) {
      return;
    }
    const { enrollment } = uploader;
    const { certificate } = enrollment;
    if (!certificate) {
      // upload requires automatic certificate to be generated first
      showErrorNotificationAction('No certificate found. Please generate certificates first');
      return;
    }
    const file = input && input.files && input.files[0];
    if (!file || !trialId || !uploader) {
      return;
    }
    const renamedFile = new File([file], `${trialId}-${uploader.enrollment.id}-${file.name}`);
    const filename = renamedFile.name;
    const uploadFunc = async (container: string) => {
      const formData = new FormData();
      formData.append('file', renamedFile);
      return ApiClient.uploadFile(container, formData);
    };
    try {
      // step 1: remove existing certificate
      await ApiClient.deleteFile(CERTIFICATES_CONTAINER_NAME, certificate.file);
    } catch (e) { /**/ }
    try {
      // step 2: upload manual certificate
      await uploadFunc(CERTIFICATES_CONTAINER_NAME);
      // step 3: update Certificate.file
      await ApiClient.updateCertificate({
        ...certificate,
        file: filename,
        certificateTemplateId: undefined,
      });
    } catch (e) {
      showErrorNotificationAction((e as Error).message);
    } finally {
      await this.fetchExtendedEnrollmentsToState();
      this.setState({ uploader: undefined });
    }
  };

  cancelEnrollment(enrollmentId: number) {
    const { cancelEnrollment: cancelEnrollmentAction } = this.props;
    return cancelEnrollmentAction(enrollmentId)
      .then(this.fetchExtendedEnrollmentsToState);
  }

  cancelInvoice(enrollmentId: number) {
    const { cancelInvoice: cancelInvoiceAction } = this.props;
    return cancelInvoiceAction(enrollmentId)
      .then(this.fetchExtendedEnrollmentsToState);
  }

  rejectEnrollment(enrollmentId: number) {
    const { rejectEnrollment: rejectEnrollmentAction } = this.props;
    return rejectEnrollmentAction(enrollmentId)
      .then(this.fetchExtendedEnrollmentsToState);
  }

  approveEnrollment(enrollmentId: number) {
    const { approveEnrollment: approveEnrollmentAction } = this.props;
    return approveEnrollmentAction(enrollmentId)
      .then(this.fetchExtendedEnrollmentsToState);
  }

  renderActionsForAppliedStatus = (enrollmentId: number) => (
    <div className="pa2 flex justify-end">
      <Button
        variant="contained"
        color="primary"
        sx={{ mx: 1 }}
        onClick={() => this.approveEnrollment(enrollmentId)}
      >
        <FormattedMessage id="trialdetails.actionApprove" />
      </Button>
      <Button
        variant="contained"
        color="secondary"
        sx={{ mx: 1 }}
        onClick={() => this.rejectEnrollment(enrollmentId)}
      >
        <FormattedMessage id="trialdetails.actionReject" />
      </Button>
    </div>
  );

  renderEnrollmentActions = (enrollment: ExtendedEnrollment, trialWithSurvey: ExtendedTrial) => {
    const {
      recreateCertificate: recreateCertificateAction,
      approveCertificate: approveCertificateAction,
      unapproveCertificate: unapproveCertificateAction,
      showNotification: showNotificationAction,
      showErrorNotification: showErrorNotificationAction,
      setLoading: setLoadingAction,
      isEditorPrivileged,
      intl,
    } = this.props;
    const {
      downloadInvoiceFilename,
      replaceFileName,
      currentCertificate,
      selectedReview,
      unapproveCertificteReason,
      unapproveCertificate: unapproveCertificateState,
      detailedEvaluation: detailedEvaluationState,
      showAllowPostResubmitDialog,
      showDigitalEvaluation,
      auswertungSetting,
      uploader,
    } = this.state;
    const {
      id: trialId,
      isDRT,
      version = 1,
      deactivate: trialDeactivated,
    } = trialWithSurvey;
    const {
      invoices, surveySubmissionMeta, submission, status,
      id: enrollmentId, certificate, thirdPartyEnrollment,
    } = enrollment;
    const uncanceledInvoice = invoices && invoices.find((invoice) => !invoice.cancelledAt);

    if (status === 'applied') {
      return this.renderActionsForAppliedStatus(enrollmentId);
    }

    let disableShowCertificate;
    if (isDRT) {
      disableShowCertificate = !certificate;
    } else if (version > 1) {
      disableShowCertificate = !certificate || !surveySubmissionMeta?.finalized;
    } else {
      disableShowCertificate = !certificate || !submission?.finalised;
    }

    const disableAuswertung = version > 1 ? false : (
      !enrollment.certificate || !!enrollment.thirdPartyEnrollment
    );

    let postResubmitTooltipId;
    if (trialWithSurvey.deactivate) {
      postResubmitTooltipId = 'trialdetails.trialIsDeactivated';
    } else if (enrollment.certificate?.approved) {
      postResubmitTooltipId = 'trialdetails.certificatedIsApproved';
    } else if (enrollment.status !== 'approved') {
      postResubmitTooltipId = 'trialdetails.enrollmentCancelled';
    }

    const disableCertificateGenerate = () => {
      // disable generation button anyway if conditions are like below
      if (isDRT || trialDeactivated || (certificate && certificate.approved)) {
        return true;
      }

      // always allow to generate certificate if user is editor privileged
      if (isEditorPrivileged) {
        return false;
      }

      // the rest remain the same as previous logic
      return ((!certificate && !thirdPartyEnrollment)
        || (!submission?.finalised && !surveySubmissionMeta?.finalized));
    };

    return (
      <Grow in>
        <Box sx={{ my: 1 }}>
          <DownloadDialog
            isOpen={!!downloadInvoiceFilename}
            onClose={() => this.setState({
              downloadInvoiceFilename: undefined,
              replaceFileName: undefined,
            })}
            container="invoices"
            file={downloadInvoiceFilename}
            replaceFileName={replaceFileName}
          />
          <Box display="flex" flexWrap="wrap" gap={1}>
            <Button
              variant="contained"
              color="primary"
              disabled={TrialDetailsView.downloadInvoiceDisabled(invoices)}
              onClick={() => {
                if (uncanceledInvoice) {
                  this.getInvoicePdfFileName(uncanceledInvoice);
                } else {
                  const sortedBy = invoices.sort(
                    (a, b) => Number(b.invoiceNumber.substring(b.invoiceNumber.lastIndexOf('-')))
                      - Number(a.invoiceNumber.substring(a.invoiceNumber.lastIndexOf('-'))),
                  );
                  const latestInvoice = sortedBy[0];
                  this.getInvoicePdfFileName(latestInvoice);
                }
              }}
            >
              <FormattedMessage id="trialdetails.downloadInvoice" />
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={TrialDetailsView.downloadInvoiceDisabled(invoices)}
              onClick={() => {
                if (uncanceledInvoice) {
                  this.getEInvoiceFileName(uncanceledInvoice);
                }
              }}
            >
              <FormattedMessage id="enrollmentsmanager.downloadEInvoiceButton" />
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={
                !enrollment.invoices
                || enrollment.invoices.length === 0
                || enrollment.invoices.filter((invoice) => invoice.cancelledAt).length === 0
              }
              onClick={() => {
                const sortedBy = enrollment.invoices
                  .filter((invoice) => invoice.cancelledAt).sort(
                    (a, b) => Number(b.invoiceNumber.substring(b.invoiceNumber.lastIndexOf('-')))
                      - Number(a.invoiceNumber.substring(a.invoiceNumber.lastIndexOf('-'))),
                  );
                this.setState(
                  {
                    downloadInvoiceFilename: sortedBy[0].fileName,
                    replaceFileName: sortedBy[0].invoiceNumber,
                  },
                );
              }}
            >
              <FormattedMessage id="trialdetails.downloadCancelledInvoice" />
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={
                (
                  trialDeactivated
                  || !enrollment.invoices
                  || !enrollment.invoices.find((invoice) => !invoice.cancelledAt)
                  || !enrollment.invoices.find((invoice) => !invoice.cancelledAt)?.fileName
                )
              }
              onClick={() => this.setState({
                promptCancel: {
                  enrollmentId: enrollment.id,
                  trigger: 'invoice',
                },
              })}
            >
              <FormattedMessage id="trialdetails.cancelInvoice" />
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={enrollment.status === 'cancelled' || !!enrollment.certificate}
              onClick={() => this.setState({
                promptCancel: {
                  enrollmentId: enrollment.id,
                  trigger: 'enrollment',
                },
              })}
            >
              <FormattedMessage id="trialdetails.actionCancel" />
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={disableCertificateGenerate()}
              onClick={() => recreateCertificateAction(enrollment)
                .then(this.fetchExtendedEnrollmentsToState)
                .then(showNotificationAction(intl.messages['trialdetails.recreateCertificateSuccess']))}
            >
              <FormattedMessage id="trialdetails.recreateCertificate" />
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={disableShowCertificate}
              onClick={() => this.setState({ currentCertificate: enrollment.certificate })}
            >
              <FormattedMessage id="trialdetails.showCertificate" />
            </Button>
            <DownloadDialog
              isOpen={!!currentCertificate}
              onClose={() => this.setState({ currentCertificate: undefined })}
              container="certificates"
              file={currentCertificate && currentCertificate.file}
              id={currentCertificate && currentCertificate.id}
            />
            {
              !isDRT && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={disableShowCertificate || certificate?.approved}
                    onClick={async () => {
                      await approveCertificateAction(enrollment.id);
                      this.fetchExtendedEnrollmentsToState();
                    }}
                  >
                    <FormattedMessage id="trialdetails.approveCertificate" />
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!submission && version !== 2}
                    onClick={() => this.showSubmission(enrollment, trialWithSurvey)}
                  >
                    <FormattedMessage id="trialdetails.actionShowSubmission" />
                  </Button>
                </>
              )
            }
            <Button
              color="primary"
              variant="contained"
              id={`DetailedEvaluation-${enrollment.id}`}
              disabled={disableAuswertung}
              onClick={async () => {
                if (version > 1 && !isDRT) {
                  redirectInternalNewTab(`trialdetail/${trialId}/enrollment/${enrollment.id}/evaluation`);
                } else if (version > 1 && isDRT) {
                  this.setState({ showDigitalEvaluation: true });
                } else {
                  const evaluation = await ApiClient.getDetailedEvaluation(enrollment.id);
                  this.setState({ detailedEvaluation: evaluation });
                }
              }}
            >
              <FormattedMessage id="enrollmentsmanager.detailedEvaluationButton" />
            </Button>
            {
              detailedEvaluationState && (
                <EvaluationDialog
                  enrollment={enrollment}
                  trial={trialWithSurvey}
                  evaluation={detailedEvaluationState}
                  auswertungSetting={auswertungSetting}
                  onClose={() => this.setState({ detailedEvaluation: undefined })}
                />
              )
            }
            {
              showDigitalEvaluation && isDRT && (
                <DigitalEvaluationDialogDRT
                  admin
                  trialId={trialWithSurvey.id}
                  enrollmentId={enrollment.id}
                  onClose={() => this.setState({ showDigitalEvaluation: false })}
                // aggregatedSubmission={aggregatedSubmission}
                />
              )
            }
            {
              !isDRT && (
                <Tooltip title={postResubmitTooltipId ? <FormattedMessage id={postResubmitTooltipId} /> : ''}>
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={TrialDetailsView.isPostResubmitActive(enrollment) ? { bgcolor: 'orange' } : null}
                      disabled={!!postResubmitTooltipId}
                      onClick={() => this.setState({ showAllowPostResubmitDialog: true })}
                    >
                      <FormattedMessage id="trialdetails.allowPostResubmit" />
                    </Button>
                  </span>
                </Tooltip>
              )
            }
            {
              showAllowPostResubmitDialog && (
                <PostResubmitDialog
                  isOpen={showAllowPostResubmitDialog}
                  onClose={() => this.setState({ showAllowPostResubmitDialog: false },
                    this.fetchExtendedEnrollmentsToState)}
                  enrollment={enrollment}
                  extendedSubmissionDate={enrollment?.extendedSubmissionDate}
                  trialCode={trialWithSurvey.code}
                  trialEndDate={trialWithSurvey.endDate}
                  user={enrollment.quipUser}
                />
              )
            }
            {
              selectedReview && (
                <ReviewDialog
                  isOpen={!!selectedReview}
                  review={selectedReview}
                  reviewRequest={selectedReview.reviewRequest}
                  onClose={() => this.setState({ selectedReview: undefined })}
                  survey={trialWithSurvey.survey}
                  evaluation={enrollment.certificate?.evaluation}
                  submission={enrollment.submission}
                />
              )
            }
            {
              !isDRT && (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!(enrollment.submission && enrollment.submission.review)}
                  onClick={() => this.setState({ selectedReview: enrollment.submission?.review })}
                >
                  <FormattedMessage id="trialdetails.showReview" />
                </Button>
              )
            }
            <Button
              variant="contained"
              color="primary"
              disabled={!certificate || !certificate.approved || isDRT}
              onClick={() => this.setState({ unapproveCertificate: enrollment.certificate })}
            >
              <FormattedMessage id="trialdetails.unapproveCertificate" />
            </Button>
            {
              trialWithSurvey.trialCategory === TrialCategoryValue.internalTrial && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => this.setState({
                    showInternalUserDialog: true,
                    enrollmentUserId: enrollment.attendeeId,
                    currentRole: enrollment.enrollmentRole,
                  })}
                >
                  <FormattedMessage id="trials.changeEnrollmentRole" />
                </Button>
              )
            }
            {
              trialWithSurvey.version === 2 && (
                <Button
                  variant="contained"
                  onClick={() => {
                    setLoadingAction(true);
                    ApiClient.evaluate(enrollment.id)
                      .then(() => showNotificationAction('Success'))
                      .catch((e) => showErrorNotificationAction(e.message))
                      .finally(() => setLoadingAction(false));
                  }}
                >
                  <FormattedMessage id="trialdetails.evaluate" />
                </Button>
              )
            }
            {
              trialWithSurvey.manualUploadCert && (
                <Button
                  variant="contained"
                  onClick={() => this.onUploadCertificate(enrollment)}
                  disabled={!certificate || certificate.approved || isDRT}
                >
                  <FormattedMessage id="trialdetails.uploadCertificate" />
                </Button>
              )
            }
            {
              trialWithSurvey.version === 2
              && trialWithSurvey.canExportMammaReview && (
                <Button
                  variant="contained"
                  onClick={() => {
                    setLoadingAction(true);
                    ApiClient.exportMammaEvaluation(enrollment.id)
                      .then((data) => downloadExcelSheets(
                        data,
                        `MR-${enrollment.trialId}-${enrollment.sampleset}-${moment().format('DDMMYY')}`,
                      ))
                      .catch((e) => showErrorNotificationAction(e.message))
                      .finally(() => setLoadingAction(false));
                  }}
                >
                  {/* <FormattedMessage id="trialdetails.evaluate" /> */}
                  Export Mamma review
                </Button>
              )
            }
            <GenericDialog
              maxWidth="sm"
              isOpen={!!unapproveCertificateState}
              onClose={() => this.setState({
                unapproveCertificate: undefined,
                unapproveCertificteReason: '',
              })}
              closeButtonLabel={<FormattedMessage id="genericClose" />}
              actions={[
                <Button
                  key="action-1"
                  variant="contained"
                  onClick={() => unapproveCertificateAction(
                    enrollment, unapproveCertificteReason,
                  )
                    .then(() => this.setState({
                      unapproveCertificate: undefined,
                      unapproveCertificteReason: '',
                    }))
                    .then(this.fetchExtendedEnrollmentsToState)}
                  disabled={!unapproveCertificteReason}
                  color="secondary"
                >
                  <FormattedMessage id="trialdetails.unapproveButton" />
                </Button>,
              ]}
            >
              <div style={{ margin: '1em' }}>
                <h1><FormattedMessage id="warning" /></h1>
                <div>
                  <FormattedMessage id="trialdetails.unapproveCertificateWarning" />
                </div>
                <TextField
                  sx={{ width: '100%' }}
                  // placeholder={<FormattedMessage id="trialdetails.unapproveReasonTextfield" />}
                  onChange={(event) => this.setState({
                    unapproveCertificteReason: event.target.value.trimLeft(),
                  })}
                  value={unapproveCertificteReason}
                  multiline
                  variant="standard"
                />
              </div>
            </GenericDialog>
            <FileUploadDialog
              isOpen={uploader?.show || false}
              onClose={this.onCloseUploadCertificateDialog}
              upload={this.uploadCertificateHandler}
              titleId="certificate.uploadCertificate"
              bodyElement={
                <Alert sx={{ my: 2 }} severity="warning"><FormattedMessage id="certificate.uploadWarning" /></Alert>
              }
            />
          </Box>
        </Box>
      </Grow>
    );
  };

  render() {
    const {
      trialWithSurvey,
      extendedEnrollments,
      showInternalUserDialog,
      showBulkEnrollDialog,
      showNotifyRegisteredDialog,
      enrollmentUserId,
      currentRole,
      promptCancel,
    } = this.state;
    const { digitalGroundTruthGroupById } = this.props;
    return (
      <>
        {
          trialWithSurvey
          && (
            <ContentBox
              elevation={0}
              title={<FormattedMessage id="trialdetails.header" />}
            >
              {
                trialWithSurvey.trialCategory === TrialCategoryValue.internalTrial
                && (
                  <div
                    style={{
                      textAlign: 'right',
                      display: 'flex',
                      gap: 8,
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={this.onEnrollInternalUserClick}
                    >
                      <FormattedMessage id="internalTrial.enroll" />
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={this.onBulkEnrollClick}
                    >
                      <FormattedMessage id="internalTrial.bulkRegister" />
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={this.onNotifyRegisteredUsersClick}
                    >
                      <FormattedMessage id="internalTrial.notifyRegisteredUsers" />
                    </Button>
                  </div>
                )
              }
              {
                trialWithSurvey.thirdPartyTrial
                && (
                  <div style={{ textAlign: 'right' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={this.downloadCertificateBundle}
                    >
                      <FormattedMessage id="trialdetails.downloadCertBundle" />
                    </Button>
                  </div>
                )
              }
              <Paper elevation={0}>
                <DetailsHeader trial={trialWithSurvey} />
                <>
                  {
                    this.state
                    && extendedEnrollments
                    && (
                      <ParticipantList
                        trial={trialWithSurvey}
                        enrollments={extendedEnrollments}
                        renderEnrollmentActions={this.renderEnrollmentActions}
                        digitalGroundTruthGroupById={digitalGroundTruthGroupById}
                      />
                    )
                  }
                </>
              </Paper>
              {
                trialWithSurvey && showInternalUserDialog
                && (
                  <EnrollInternalUserDialog
                    isOpen={showInternalUserDialog}
                    trialId={trialWithSurvey.id}
                    closeDialog={this.closeInternalTrialEnrollDialog}
                    onSuccess={extendedEnrollments && this.fetchExtendedEnrollmentsToState}
                    enrollmentUserId={enrollmentUserId}
                    currentRole={currentRole}
                  />
                )
              }
              {
                trialWithSurvey && showBulkEnrollDialog
                && (
                  <BulkInternalEnrollDialog
                    isOpen={showBulkEnrollDialog}
                    trialId={trialWithSurvey.id}
                    trialName={trialWithSurvey.name}
                    closeDialog={this.closeBulkEnrollDialog}
                    onSuccess={extendedEnrollments && this.fetchExtendedEnrollmentsToState}
                  />
                )
              }
              {
                trialWithSurvey && showNotifyRegisteredDialog
                && (
                  <RegisteredUsersNotifyDialog
                    isOpen={showNotifyRegisteredDialog}
                    enrollments={extendedEnrollments}
                    closeDialog={this.closeNotifyRegisteredDialog}
                    onSendEmail={this.handleNotifyRegisteredSendEmail}
                  />
                )
              }
            </ContentBox>
          )
        }
        <Prompt
          action={this.promptHandler}
          display={!!promptCancel}
          title={(() => {
            const { trigger } = promptCancel || {};
            if (trigger === 'invoice') {
              return <FormattedMessage id="trialdetails.cancelInvoice" />;
            }
            if (trigger === 'enrollment') {
              return <FormattedMessage id="trialdetails.actionCancel" />;
            }
            return '';
          })()}
        >
          {
            promptCancel?.trigger === 'invoice' && (
              <FormattedMessage id="trialdetails.confirmCancelInvoice" />
            )
          }
          {
            promptCancel?.trigger === 'enrollment' && (
              <FormattedMessage id="trialdetails.confirmCancelEnrollment" />
            )
          }
        </Prompt>
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  locale: state.intl.locale,
  trialCategories: state.trialCategories.categories,
  digitalGroundTruthGroupById: state.SurveyData.digitalGroundTruthGroup.byId,
  isEditorPrivileged: selectIsEditorPrivileged(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  approveEnrollment: (enrollmentId: number) => dispatch(approveEnrollment(enrollmentId)),
  rejectEnrollment: (enrollmentId: number) => dispatch(rejectEnrollment(enrollmentId)),
  cancelInvoice: (enrollmentId: number) => dispatch(cancelInvoice(enrollmentId)),
  cancelEnrollment: (enrollmentId: number) => dispatch(cancelEnrollment(enrollmentId)),
  approveCertificate: (enrollmentId: number) => dispatch(approveCertificate(enrollmentId)),
  recreateCertificate:
    (enrollment: ExtendedEnrollment) => dispatch(recreateCertificate(enrollment)),
  unapproveCertificate: (enrollment: ExtendedEnrollment, reason?: string) => dispatch(
    unapproveCertificate(enrollment.certificate, reason),
  ),
  showErrorNotification: (
    message: string | React.ReactElement,
  ) => dispatch(showErrorNotification(message)),
  showNotification: (
    message: string | React.ReactElement,
  ) => dispatch(showNotification(message)),
  setLoading: (spiral: boolean) => dispatch(setLoading(spiral)),
  fetchDigitalGroundTruthGroup:
    (surveyId: number) => dispatch(fetchDigitalGroundTruthGroups(surveyId)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type MatchParams = {
  trialId: string;
};
type Props = PropsFromRedux & RouteType<MatchParams> & { intl: any };

export default injectIntl(connector(withRouter(TrialDetailsView)));
